import React, { useState, useEffect, useContext } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { AuthContext, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Users, Shield, CheckCircle, XCircle, Clock, Search, RefreshCw, Building2, FileText, Activity } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const UserManagement = () => {
  const { token, user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [franchisees, setFranchisees] = useState([]);
  const [allFranchisees, setAllFranchisees] = useState([]); // For HQ Admin to see all branches
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditStats, setAuditStats] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityStats, setActivityStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [assignBranchDialog, setAssignBranchDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [emailSearch, setEmailSearch] = useState('');
  const [activityFilter, setActivityFilter] = useState('all');

  // Helper function for role badge colors
  const getRoleBadgeStyle = (role) => {
    const styles = {
      'HQ_ADMIN': 'bg-red-600 text-white hover:bg-red-700',
      'SYSTEM_ADMIN': 'bg-purple-600 text-white hover:bg-purple-700',
      'REGIONAL_COORDINATOR': 'bg-blue-600 text-white hover:bg-blue-700',
      'FRANCHISEE_OPERATOR': 'bg-emerald-600 text-white hover:bg-emerald-700',
      'FRANCHISEE_MANAGER': 'bg-cyan-600 text-white hover:bg-cyan-700',
      'FRANCHISEE_SUPERVISOR': 'bg-slate-500 text-white hover:bg-slate-600',
    };
    return styles[role] || 'bg-gray-500 text-white';
  };

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: '',
    phone: '',
    franchisee_id: '',
    assigned_branches: []
  });

  useEffect(() => {
    fetchUsers();
    fetchFranchisees();
    if (['HQ_ADMIN', 'SYSTEM_ADMIN'].includes(user?.role)) {
      fetchAllFranchisees();
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === 'audit') {
      fetchAuditLogs();
      fetchAuditStats();
    } else if (activeTab === 'activity') {
      fetchActivityLogs();
      fetchActivityStats();
    }
  }, [activeTab, statusFilter, activityFilter]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchFranchisees = async () => {
    try {
      const response = await axios.get(`${API}/franchisees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFranchisees(response.data);
    } catch (error) {
      console.error('Failed to fetch franchisees');
    }
  };

  const fetchAllFranchisees = async () => {
    // HQ Admin/System Admin need to see all franchisees for assignment
    try {
      const response = await axios.get(`${API}/franchisees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllFranchisees(response.data);
    } catch (error) {
      console.error('Failed to fetch all franchisees');
    }
  };

  const openAssignBranchDialog = (targetUser) => {
    setSelectedUser(targetUser);
    setSelectedBranches(targetUser.assigned_branches || []);
    setAssignBranchDialog(true);
  };

  const handleBranchToggle = (branchId) => {
    setSelectedBranches(prev => {
      if (prev.includes(branchId)) {
        return prev.filter(id => id !== branchId);
      } else {
        return [...prev, branchId];
      }
    });
  };

  const handleAssignBranches = async () => {
    try {
      await axios.patch(`${API}/users/${selectedUser.user_id}/assign-branches`, 
        selectedBranches,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Branches assigned successfully');
      setAssignBranchDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign branches');
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', '100');
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (emailSearch) params.append('email_filter', emailSearch);
      
      const response = await axios.get(`${API}/audit-logs/login?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditLogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    }
  };

  const fetchAuditStats = async () => {
    try {
      const response = await axios.get(`${API}/audit-logs/login/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditStats(response.data);
    } catch (error) {
      console.error('Failed to fetch audit stats');
    }
  };

  const fetchActivityLogs = async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', '100');
      if (activityFilter !== 'all') params.append('action_filter', activityFilter);
      
      const response = await axios.get(`${API}/audit-logs/activity?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivityLogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch activity logs');
    }
  };

  const fetchActivityStats = async () => {
    try {
      const response = await axios.get(`${API}/audit-logs/activity/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivityStats(response.data);
    } catch (error) {
      console.error('Failed to fetch activity stats');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User created successfully');
      setOpenDialog(false);
      fetchUsers();
      setFormData({ email: '', password: '', full_name: '', role: '', phone: '', franchisee_id: '', assigned_branches: [] });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const toggleUserStatus = async (userId) => {
    try {
      await axios.patch(`${API}/users/${userId}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User status updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleSearchAudit = () => {
    fetchAuditLogs();
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="user-management-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-heading font-bold">User Management</h1>
            <p className="text-muted-foreground mt-1">Manage system users and view login activities</p>
          </div>
          <Dialog open={openDialog} onOpenChange={setOpenDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-user-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Full Name*</Label>
                  <Input value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} required data-testid="full-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Email*</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required data-testid="email-input" />
                </div>
                <div className="space-y-2">
                  <Label>Password*</Label>
                  <Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required data-testid="password-input" />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} data-testid="phone-input" />
                </div>
                <div className="space-y-2">
                  <Label>Role*</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})} required>
                    <SelectTrigger data-testid="role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* HQ_ADMIN and SYSTEM_ADMIN can create any role */}
                      {['HQ_ADMIN', 'SYSTEM_ADMIN'].includes(user?.role) && (
                        <>
                          <SelectItem value="HQ_ADMIN">HQ Admin</SelectItem>
                          <SelectItem value="SYSTEM_ADMIN">System Admin</SelectItem>
                          <SelectItem value="REGIONAL_COORDINATOR">Regional Coordinator</SelectItem>
                          <SelectItem value="FRANCHISEE_OPERATOR">Franchisee Operator</SelectItem>
                        </>
                      )}
                      {/* FRANCHISEE_OPERATOR can only create Manager and Supervisor for their branch */}
                      {user?.role === 'FRANCHISEE_OPERATOR' && (
                        <>
                          <SelectItem value="FRANCHISEE_MANAGER">Franchisee Manager</SelectItem>
                          <SelectItem value="FRANCHISEE_SUPERVISOR">Franchisee Supervisor</SelectItem>
                        </>
                      )}
                      {/* Everyone who can create users can create these lower roles */}
                      {['HQ_ADMIN', 'SYSTEM_ADMIN'].includes(user?.role) && (
                        <>
                          <SelectItem value="FRANCHISEE_MANAGER">Franchisee Manager</SelectItem>
                          <SelectItem value="FRANCHISEE_SUPERVISOR">Franchisee Supervisor</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                {['FRANCHISEE_OPERATOR', 'FRANCHISEE_MANAGER', 'FRANCHISEE_SUPERVISOR', 'REGIONAL_COORDINATOR'].includes(formData.role) && (
                  <div className="space-y-2">
                    <Label>{formData.role === 'REGIONAL_COORDINATOR' ? 'Assigned Branches' : 'Branch'}*</Label>
                    <Select value={formData.franchisee_id} onValueChange={(value) => setFormData({...formData, franchisee_id: value, assigned_branches: [value]})} required>
                      <SelectTrigger data-testid="franchisee-select">
                        <SelectValue placeholder="Select branch" />
                      </SelectTrigger>
                      <SelectContent>
                        {franchisees.map((f) => (
                          <SelectItem key={f.franchisee_id} value={f.franchisee_id}>
                            {f.company_name} ({f.franchise_id})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>Cancel</Button>
                  <Button type="submit" data-testid="submit-user-btn">Create User</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="users">
              <Users className="h-4 w-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="audit">
              <Shield className="h-4 w-4 mr-2" />
              Login Audit
            </TabsTrigger>
            <TabsTrigger value="activity">
              <FileText className="h-4 w-4 mr-2" />
              Activity Audit
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>All Users</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center text-muted-foreground">Loading...</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Branches</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((targetUser) => (
                        <TableRow key={targetUser.user_id} data-testid={`user-row-${targetUser.email}`}>
                          <TableCell className="font-medium">{targetUser.full_name}</TableCell>
                          <TableCell>{targetUser.email}</TableCell>
                          <TableCell>
                            <Badge className={getRoleBadgeStyle(targetUser.role)}>
                              {targetUser.role.replace(/_/g, ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {targetUser.assigned_branches?.length > 0 ? (
                              <span className="text-sm text-muted-foreground">
                                {targetUser.assigned_branches.length} branch(es)
                              </span>
                            ) : (
                              <span className="text-sm text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={targetUser.is_active ? 'default' : 'destructive'}>
                              {targetUser.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>{new Date(targetUser.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {/* Assign Branches button - only for Regional Coordinators, Managers, Supervisors */}
                              {['REGIONAL_COORDINATOR', 'FRANCHISEE_MANAGER', 'FRANCHISEE_SUPERVISOR'].includes(targetUser.role) && 
                               ['HQ_ADMIN', 'SYSTEM_ADMIN', 'FRANCHISEE_OPERATOR'].includes(user?.role) && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => openAssignBranchDialog(targetUser)}
                                  title="Assign Branches"
                                >
                                  <Building2 className="h-4 w-4" />
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => toggleUserStatus(targetUser.user_id)}
                                data-testid={`toggle-user-${targetUser.email}`}
                              >
                                {targetUser.is_active ? 'Deactivate' : 'Activate'}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit" className="space-y-4">
            {/* Stats Cards */}
            {auditStats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Successful Logins</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="text-3xl font-bold text-green-600">{auditStats.total_logins}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{auditStats.period}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Failed Logins</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      <XCircle className="h-5 w-5 text-red-500" />
                      <span className="text-3xl font-bold text-red-600">{auditStats.failed_logins}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{auditStats.period}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Unique Users</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-blue-500" />
                      <span className="text-3xl font-bold text-blue-600">{auditStats.unique_users}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{auditStats.period}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex gap-4 items-end">
                  <div className="flex-1 max-w-xs">
                    <Label className="text-sm">Search by Email</Label>
                    <div className="relative mt-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input 
                        placeholder="Enter email..." 
                        value={emailSearch}
                        onChange={(e) => setEmailSearch(e.target.value)}
                        className="pl-10"
                        onKeyDown={(e) => e.key === 'Enter' && handleSearchAudit()}
                      />
                    </div>
                  </div>
                  <div className="w-40">
                    <Label className="text-sm">Status</Label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="success">Success</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleSearchAudit} variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Audit Log Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Login Activity Log
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>IP Address</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          No login activities recorded yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      auditLogs.map((log) => (
                        <TableRow key={log.log_id}>
                          <TableCell className="text-sm">
                            <div className="flex items-center gap-2">
                              <Clock className="h-3 w-3 text-muted-foreground" />
                              {formatTimestamp(log.timestamp)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{log.user_name || 'Unknown'}</p>
                              <p className="text-xs text-muted-foreground">{log.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            {log.user_role ? (
                              <Badge className={getRoleBadgeStyle(log.user_role)}>
                                {log.user_role.replace(/_/g, ' ')}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{log.action.replace('_', ' ')}</Badge>
                          </TableCell>
                          <TableCell>
                            {log.status === 'success' ? (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Success
                              </Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800">
                                <XCircle className="h-3 w-3 mr-1" />
                                Failed
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{log.ip_address || '-'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                            {log.details}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Audit Tab */}
          <TabsContent value="activity" className="space-y-4">
            {/* Stats Cards */}
            {activityStats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Activities</p>
                        <p className="text-2xl font-bold">{activityStats.total_activities}</p>
                      </div>
                      <Activity className="h-8 w-8 text-blue-500" />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">{activityStats.period}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">By Action Type</p>
                      <div className="space-y-1">
                        {activityStats.action_breakdown && Object.entries(activityStats.action_breakdown).slice(0, 4).map(([action, count]) => (
                          <div key={action} className="flex justify-between text-sm">
                            <span className="capitalize">{action.toLowerCase().replace(/_/g, ' ')}</span>
                            <span className="font-medium">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">By Entity Type</p>
                      <div className="space-y-1">
                        {activityStats.entity_breakdown && Object.entries(activityStats.entity_breakdown).slice(0, 4).map(([entity, count]) => (
                          <div key={entity} className="flex justify-between text-sm">
                            <span className="capitalize">{entity}</span>
                            <span className="font-medium">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex gap-4 items-center">
                  <Select value={activityFilter} onValueChange={setActivityFilter}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Filter by action" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Actions</SelectItem>
                      <SelectItem value="CREATE">Create</SelectItem>
                      <SelectItem value="UPDATE">Update</SelectItem>
                      <SelectItem value="DELETE">Delete</SelectItem>
                      <SelectItem value="APPROVE">Approve</SelectItem>
                      <SelectItem value="REJECT">Reject</SelectItem>
                      <SelectItem value="ASSIGN">Assign</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={fetchActivityLogs}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Activity Log Table */}
            <Card>
              <CardHeader>
                <CardTitle>Activity Audit Log</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activityLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground">
                          No activity logs found
                        </TableCell>
                      </TableRow>
                    ) : (
                      activityLogs.map((log) => (
                        <TableRow key={log.log_id}>
                          <TableCell className="whitespace-nowrap text-sm">
                            {new Date(log.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{log.user_name}</p>
                              <p className="text-xs text-muted-foreground">{log.user_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={`text-xs ${getRoleBadgeStyle(log.user_role)}`}>
                              {log.user_role?.replace(/_/g, ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={
                                log.action === 'CREATE' ? 'default' :
                                log.action === 'DELETE' ? 'destructive' :
                                log.action === 'APPROVE' ? 'default' :
                                log.action === 'REJECT' ? 'destructive' :
                                'secondary'
                              }
                              className={
                                log.action === 'CREATE' ? 'bg-green-500' :
                                log.action === 'APPROVE' ? 'bg-green-500' :
                                ''
                              }
                            >
                              {log.action}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="text-sm capitalize">{log.entity_type}</p>
                              {log.entity_name && (
                                <p className="text-xs text-muted-foreground">{log.entity_name}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                            {log.details || '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Assign Branches Dialog */}
        <Dialog open={assignBranchDialog} onOpenChange={setAssignBranchDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Assign Branches to {selectedUser?.full_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Role: <Badge className={getRoleBadgeStyle(selectedUser?.role)}>{selectedUser?.role?.replace(/_/g, ' ')}</Badge>
              </div>
              <div className="space-y-2">
                <Label>Select Branches</Label>
                <div className="max-h-64 overflow-y-auto border rounded-lg p-2 space-y-2">
                  {(allFranchisees.length > 0 ? allFranchisees : franchisees).map((branch) => (
                    <label 
                      key={branch.franchisee_id} 
                      className="flex items-center gap-3 p-2 rounded hover:bg-muted cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedBranches.includes(branch.franchisee_id)}
                        onChange={() => handleBranchToggle(branch.franchisee_id)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <div>
                        <p className="font-medium">{branch.company_name}</p>
                        <p className="text-xs text-muted-foreground">{branch.franchise_id} - {branch.kindergarten?.location}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm">
                  <strong>Selected:</strong> {selectedBranches.length} branch(es)
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setAssignBranchDialog(false)}>Cancel</Button>
                <Button onClick={handleAssignBranches}>Save Assignments</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default UserManagement;
